import { IconPlayTvguide } from '@assets/icon-play-tvguide';
import { FallbackCard } from '@components/atoms/FallbackCard/FallbackCard';
import { TZDate } from '@date-fns/tz';
import type { GetChannelProgramQuery } from '@lib/graphql/generated';
import { createDate, isCurrent } from '@utils/dateHelpers';
import { getShowtimeWatchUrl } from '@utils/showtimeWatchUrl';
import { Image } from '@vgn-medien-holding/vgn-fe-components';
import { format } from 'date-fns';
import { de } from 'date-fns/locale/de';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export interface ChannelShowtimeProps {
  showtime: NonNullable<GetChannelProgramQuery['channelShowtimes']>['data'][number];
  onAction: (tvChannelShowtime) => void;
}

export const ChannelShowtime = ({ showtime, onAction }: ChannelShowtimeProps) => {
  let progress = 0;
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    setCurrentTime(new TZDate(new Date(), 'Europe/Vienna').getTime());
    const interval = setInterval(() => {
      setCurrentTime(new TZDate(new Date(), 'Europe/Vienna').getTime());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  if (showtime) {
    const startTime = createDate(showtime?.start).getTime();
    const endTime = createDate(showtime?.stop).getTime();

    if (currentTime > endTime || currentTime < startTime) {
      progress = -1;
    } else {
      progress = Math.trunc(((currentTime - startTime) / (endTime - startTime)) * 100);
    }
  }

  const startTime = createDate(showtime.start).getTime();
  const watchUrl = getShowtimeWatchUrl(showtime, currentTime);
  const timeLabel = (
    <span className="inline-flex items-center gap-2">
      {watchUrl && (
        <span className="inline-flex" role="img" aria-label="Auf Joyn anschauen">
          <IconPlayTvguide classProps={{ root: 'text-white' }} />
        </span>
      )}
      {format(startTime, 'HH:mm', { locale: de })}
    </span>
  );

  const baseStyle = twMerge(
    'w-full min-w-0 cursor-pointer border border-transparent p-4 hover:border-primary rounded-lg',
    isCurrent(showtime.start, showtime.stop, currentTime)
      ? 'bg-gray-600 p-0'
      : 'bg-gray-700 grid grid-cols-[auto_auto_1fr] gap-3 items-center',
  );

  return isCurrent(showtime.start, showtime.stop, currentTime) ? (
    <div
      className={baseStyle}
      role="button"
      tabIndex={0}
      onClick={() => {
        onAction(showtime);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onAction(showtime);
      }}
    >
      <div className="aspect-video p-4 pb-0">
        <div className="relative size-full overflow-hidden rounded-lg">
          {showtime?.image?.url ? (
            <Image
              src={showtime.image.url}
              copyright=""
              alt={showtime.title}
              fill
              sizes="(min-width: 768px) 300px, 100vw"
            />
          ) : (
            <FallbackCard />
          )}
        </div>
      </div>
      {progress >= 0 && (
        <div className="mt-2 h-2 w-full bg-primary/10">
          <div
            className="relative h-full bg-primary transition-size duration-100"
            style={{ width: `${progress || 0}%` }}
          >
            <div className="absolute -top-2 -right-2 grid size-5 animate-pulse place-items-center rounded-full bg-primary/60"></div>
            <div className="absolute -top-1 -right-1 grid size-3 place-items-center rounded-full bg-primary"></div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-[auto_auto_1fr] items-center gap-3 p-4">
        <div className="font-bold text-primary text-sm tracking-2px">{timeLabel}</div>
        <div className="inline-block h-4 w-px bg-gray-400"></div>
        <div>{showtime.title}</div>
      </div>
    </div>
  ) : (
    <div
      className={baseStyle}
      role="button"
      tabIndex={0}
      onClick={() => {
        onAction(showtime);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onAction(showtime);
      }}
    >
      <div className="font-bold text-gray-400 text-sm tracking-2px">{timeLabel}</div>
      <div className="inline-block h-4 w-px bg-gray-400"></div>
      <div>{showtime.title}</div>
    </div>
  );
};
